import moment from 'moment';
import { COLUMN_TYPE } from '../../../../utils/epplusXlsxWriter';

// Mirrors the API's Exam Data export (ExportHelper.ExportExamDataToCSV): the same three sheets,
// columns, header text and cell text the server produced from ExamDataModel, so the workbook
// built in the browser matches the one the API used to return.

const toText = (value) => (value === null || value === undefined ? '' : String(value));

// DateColumns in ConvertDataTableTo2DArrayWithHeaders -> MM/dd/yyyy.
const toDateText = (value) => {
    if (!value) return '';
    const date = moment.parseZone(value);
    return date.isValid() ? date.format('MM/DD/YYYY') : '';
};

// Any other DateTime -> DateTime.ToString() on the (en-US) API server.
const toDateTimeText = (value) => {
    if (!value) return '';
    const date = moment.parseZone(value);
    return date.isValid() ? date.format('M/D/YYYY h:mm:ss A') : '';
};

const toYesNo = (value) => (value === true || value === 'True' || value === 'true' ? 'Yes' : 'No');
const toYN = (value) => (value === true || value === 'True' || value === 'true' ? 'Y' : 'N');

// RVU is DECIMAL(18,2) in SQL, so its .NET decimal text always carries two decimals.
const toDecimal2Text = (value) => (value === null || value === undefined ? '' : Number(value).toFixed(2));

// "Exam Data" and "Excluded Data" sheets: ExamDataModel columns minus the export's removed ones.
const EXAM_DATA_COLUMNS = [
    { key: 'WorkListName', header: 'Worklist', format: toText },
    { key: 'SiteName', header: 'Facility', format: toText },
    { key: 'Radiologist', header: 'Provider', format: toText },
    { key: 'ShiftName', header: 'Shift Name', format: toText },
    { key: 'Subspeciality', header: 'Subspecialty', format: toText },
    { key: 'isRemoteCoverage', header: 'Remote Coverage', format: toYesNo },
    { key: 'UnReadDayOfWeekName', header: 'Unread Day of Week', format: toText },
    { key: 'UnReadDate', header: 'Unread Date', format: toDateText },
    { key: 'UnReadHours', header: 'Unread Hour', format: toText, type: COLUMN_TYPE.INTEGER },
    { key: 'ReadDayOfWeek', header: 'Read Day of Week', format: toText },
    { key: 'ReadDate', header: 'Read Date', format: toDateText },
    { key: 'ReadHours', header: 'Read Hour', format: toText, type: COLUMN_TYPE.INTEGER },
    { key: 'SLA', header: 'SLA (Minutes)', format: toText, type: COLUMN_TYPE.INTEGER },
    { key: 'SLAOutlier', header: 'SLA Outlier', format: toText },
    { key: 'OverSLA', header: 'Over SLA (Y/N)', format: toText },
    { key: 'MinTatVSSLA', header: 'Min TAT VS SLA', format: toText, type: COLUMN_TYPE.DECIMAL },
    { key: 'SLABand', header: 'SLA Band', format: toText },
    { key: 'StudyWithinCoverage', header: 'Study Within Coverage', format: toText },
    { key: 'PriorityName', header: 'Priority', format: toText },
    { key: 'ProcedureCode', header: 'Internal Procedure Code', format: toText },
    { key: 'ProcedureName', header: 'Procedure Name', format: toText },
    { key: 'RVU', header: 'RVU', format: toDecimal2Text, type: COLUMN_TYPE.DECIMAL },
    { key: 'ModalityName', header: 'Modality', format: toText },
    { key: 'Accession', header: 'Accession', format: toText },
    { key: 'TimeRemaining', header: 'Time Remaining', format: toText },
    { key: 'UnreadDTS', header: 'Unread DTS', format: toDateTimeText },
    { key: 'FinalDTS', header: 'Final DTS', format: toDateTimeText },
    { key: 'TATUnreadOrFinalInMinutes', header: 'TAT Unread/Final In Mins', format: toText, type: COLUMN_TYPE.INTEGER },
    { key: 'Covered_Priority_Flag', header: 'Covered Priority Flag', format: toYN },
    { key: 'Use_In_Forecast', header: 'Use In Forecast', format: toYN },
    { key: 'Roll_Over', header: 'Roll Over', format: toYN },
    { key: 'SiteManualID', header: 'Unique Facility ID', format: toText },
    { key: 'NPI', header: 'NPI', format: toText },
    { key: 'RecordDate', header: 'Record Date', format: toDateText },
    { key: 'BatchNo', header: 'BatchNo', format: toText },
];

// "Unassigned Exam Data" sheet: the export copies these into string columns, so every value is
// text (DateTime/decimal ToString) and only the header-name rules below reformat it.
const UNASSIGNED_EXAM_DATA_COLUMNS = [
    { key: 'SiteName', header: 'Facility', format: toText },
    { key: 'Radiologist', header: 'Provider', format: toText },
    { key: 'ShiftName', header: 'Shift Name', format: toText },
    { key: 'Subspeciality', header: 'Subspecialty', format: toText },
    { key: 'isRemoteCoverage', header: 'Remote Coverage', format: toYesNo },
    { key: 'UnReadDayOfWeek', header: 'Unread Day of Week', format: toText },
    { key: 'UnReadDate', header: 'Unread Date', format: toDateText },
    { key: 'UnReadHours', header: 'Unread Hour', format: toText },
    { key: 'ReadDayOfWeek', header: 'Read Day of Week', format: toText },
    { key: 'ReadDate', header: 'Read Date', format: toDateText },
    { key: 'ReadHours', header: 'Read Hour', format: toText },
    { key: 'SLA', header: 'SLA (Minutes)', format: toText },
    { key: 'SLAOutlier', header: 'SLA Outlier', format: toText },
    { key: 'OverSLA', header: 'Over SLA (Y/N)', format: toText },
    { key: 'MinTatVSSLA', header: 'Min TAT VS SLA', format: toText },
    { key: 'SLABand', header: 'SLA Band', format: toText },
    { key: 'StudyWithinCoverage', header: 'Study Within Coverage', format: toText },
    { key: 'PriorityName', header: 'Priority', format: toText },
    { key: 'ProcedureCode', header: 'Internal Procedure Code', format: toText },
    { key: 'ProcedureName', header: 'Procedure Name', format: toText },
    { key: 'RVU', header: 'RVU', format: toDecimal2Text },
    { key: 'ModalityName', header: 'Modality', format: toText },
    { key: 'Accession', header: 'Accession', format: toText },
    { key: 'TimeRemaining', header: 'Time Remaining', format: toText },
    { key: 'UnreadDTS', header: 'Unread DTS', format: toDateTimeText },
    { key: 'FinalDTS', header: 'Final DTS', format: toDateTimeText },
    { key: 'TATUnreadOrFinalInMinutes', header: 'TAT Unread/Final in Mins', format: toText },
    { key: 'Covered_Priority_Flag', header: 'Covered Priority Flag', format: toYN },
    { key: 'Use_In_Forecast', header: 'Use In Forecast', format: toYN },
    { key: 'Roll_Over', header: 'Roll Over', format: toYN },
    { key: 'SiteManualID', header: 'Unique Facility ID', format: toText },
    { key: 'NPI', header: 'NPI', format: toText },
    // "Record Date" is not one of the export's date columns, so it keeps the full DateTime text.
    { key: 'RecordDate', header: 'Record Date', format: toDateTimeText },
    { key: 'BatchNo', header: 'Batch No', format: toText },
];

const toSheetDefinition = (name, columns) => ({
    name,
    headers: columns.map((column) => column.header),
    columnTypes: columns.map((column) => column.type || COLUMN_TYPE.TEXT),
    toRow: (record) => columns.map((column) => column.format(record?.[column.key])),
});

// Sheet order and API list for each sheet, as ExportExamDataToCSV writes them.
export const EXAM_DATA_EXPORT_SHEETS = [
    { listKey: 'lstExamData', ...toSheetDefinition('Exam Data', EXAM_DATA_COLUMNS) },
    { listKey: 'lstExcludedExamData', ...toSheetDefinition('Excluded Data', EXAM_DATA_COLUMNS) },
    { listKey: 'lstUnassignedExamData', ...toSheetDefinition('Unassigned Exam Data', UNASSIGNED_EXAM_DATA_COLUMNS) },
];
